import React, {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  DragEvent,
} from "react";
import { Container, Form, Button, Row, Col } from "react-bootstrap";
import { ImageData, Settings, FormatSupport } from "./types";
import { FaUpload, FaCog, FaImage, FaDownload, FaTrash } from "react-icons/fa";
// import "./ImageOptimizer.css";
import { Box } from "./imageOptimizerStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const ImageOptimizer: React.FC = () => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [settings, setSettings] = useState<Settings>({
    compressionLevel: "medium",
    format: "image/webp",
    quality: 0.8,
  });
  const [formatSupport, setFormatSupport] = useState<FormatSupport>({
    webp: true,
    avif: true,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const qualityByCompressionLevel: Record<string, number> = {
    low: 0.6,
    medium: 0.8,
    high: 0.9,
  };

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [images, formatSupport]);
  useEffect(() => {
    const checkWebPSupport = async () => {
      const webP = new Image();
      return new Promise<boolean>((resolve) => {
        webP.onload = webP.onerror = () => resolve(webP.height === 2);
        webP.src =
          "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
      });
    };

    const checkAVIFSupport = async () => {
      const avif = new Image();
      return new Promise<boolean>((resolve) => {
        avif.onload = avif.onerror = () => resolve(avif.height === 2);
        avif.src =
          "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=";
      });
    };

    const checkSupport = async () => {
      const webpSupported = await checkWebPSupport();
      const avifSupported = await checkAVIFSupport();
      setFormatSupport({ webp: webpSupported, avif: avifSupported });

      if (!webpSupported && settings.format === "image/webp") {
        setSettings((prev) => ({ ...prev, format: "image/jpeg" }));
      }
      if (!avifSupported && settings.format === "image/avif") {
        setSettings((prev) => ({ ...prev, format: "image/jpeg" }));
      }
    };

    checkSupport();
  }, []);

  const handleFiles = (files: FileList) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImages((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).substr(2, 9),
            file,
            preview: reader.result as string,
            originalSize: file.size,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragging");
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const updateSettings = (
    e: ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    field: keyof Settings
  ) => {
    const value =
      field === "scale"
        ? e.target.value
          ? Number(e.target.value) / 100
          : undefined
        : e.target.value;
    const newSettings = {
      ...settings,
      [field]: value,
      ...(field === "compressionLevel" && {
        quality: qualityByCompressionLevel[e.target.value],
      }),
    };
    setSettings(newSettings);

    // Reset optimized state for all images
    setImages((prev) =>
      prev.map((img) => {
        const { optimized, ...rest } = img;
        return rest;
      })
    );
  };

  const optimizeImage = async (id: string) => {
    const image = images.find((img) => img.id === id);
    if (!image) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.src = image.preview;
    await new Promise((resolve) => {
      img.onload = resolve;
    });

    let width = img.width;
    let height = img.height;

    if (settings.scale) {
      width *= settings.scale;
      height *= settings.scale;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    const optimizedUrl = canvas.toDataURL(settings.format, settings.quality);
    const optimizedSize = Math.round(
      (optimizedUrl.length - optimizedUrl.indexOf(",") - 1) * 0.75
    );

    const sizeIncreased = optimizedSize > image.originalSize;
    const sizeChangePercentage = sizeIncreased
      ? Math.round((optimizedSize / image.originalSize - 1) * 100)
      : Math.round((1 - optimizedSize / image.originalSize) * 100);

    setImages((prev) =>
      prev.map((img) =>
        img.id === id
          ? {
            ...img,
            optimized: {
              url: optimizedUrl,
              size: optimizedSize,
              width,
              height,
              sizeIncreased,
              sizeChangePercentage,
            },
          }
          : img
      )
    );
  };

  const downloadImage = (id: string) => {
    const image = images.find((img) => img.id === id);
    if (!image?.optimized) return;

    const link = document.createElement("a");
    link.href = image.optimized.url;
    const format = settings.format.split("/")[1];
    link.download = `optimized-${image.file.name.split(".")[0]}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  return (
    <Box>
      <Container
        className={`image-optimizer-container ${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
          }`}
          style={{paddingLeft: '0px', paddingRight: '0px'}}>
        <div className="imgopt-card">
          {!isInIframe && (
            <ToolsHeader
              title="Image Optimizer"
              description="Optimize images to enhance loading speed and boost overall SEO
                performance effectively."
            />
          )}
          {/* <h1 className="imgopt-title">
            <FaImage className="me-2" size={24} />
            Image Optimizer
          </h1> */}

          <div
            className="imgopt-upload-area"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("dragging");
            }}
            onDragLeave={(e) => e.currentTarget.classList.remove("dragging")}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*"
              className="imgopt-hidden"
              onChange={handleFileInput}
            />
            <FaUpload className="imgopt-upload-icon mb-3" size={48} />
            <p className="imgopt-upload-text">
              Drag and drop images here, or{" "}
              <Button variant="link" className="imgopt-browse-button p-0">
                browse
              </Button>
            </p>
            <p className="imgopt-upload-hint">
              Supports JPG, PNG, SVG, GIF, WebP, and AVIF
            </p>
          </div>

          <div className="imgopt-settings mt-4">
            <h2 className="imgopt-settings-title">
              <FaCog className="me-2" size={20} />
              Optimization Settings
            </h2>
            <Row className="imgopt-settings-grid">
              <Col xs={12} md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="imgopt-form-label">
                    Compression Level
                  </Form.Label>
                  <Form.Select
                    className="imgopt-form-control"
                    value={settings.compressionLevel}
                    onChange={(e) => updateSettings(e, "compressionLevel")}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="imgopt-form-label">
                    Output Format
                  </Form.Label>
                  <Form.Select
                    className="imgopt-form-control"
                    value={settings.format}
                    onChange={(e) => updateSettings(e, "format")}
                  >
                    <option value="image/webp" disabled={!formatSupport.webp}>
                      WebP {formatSupport.webp ? "" : "(not supported)"}
                    </option>
                    <option value="image/avif" disabled={!formatSupport.avif}>
                      AVIF {formatSupport.avif ? "" : "(not supported)"}
                    </option>
                    <option value="image/jpeg">JPEG</option>
                    <option value="image/png">PNG</option>
                    <option value="image/gif">GIF</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Group>
                  <Form.Label className="imgopt-form-label">
                    Scale (%)
                  </Form.Label>
                  <Form.Control
                    as="input"
                    type="number"
                    className="imgopt-form-control"
                    min="1"
                    max="100"
                    placeholder="100"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateSettings(e, "scale")
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="imgopt-image-list mt-4">
            {images.map((image) => (
              <div key={image.id} className="imgopt-image-item">
                <div className="imgopt-image-preview">
                  <img
                    src={image.optimized?.url || image.preview}
                    alt="Preview"
                  />
                </div>
                <div className="imgopt-image-info">
                  <h3 className="imgopt-image-name">{image.file.name}</h3>
                  <p className="imgopt-image-size">
                    Original: {(image.originalSize / 1024).toFixed(2)} KB
                  </p>
                  {image.optimized && (
                    <>
                      <p
                        className={
                          image.optimized.sizeIncreased
                            ? "imgopt-error-size"
                            : "imgopt-optimized-size"
                        }
                      >
                        Optimized: {(image.optimized.size / 1024).toFixed(2)} KB
                        (
                        {image.optimized.sizeIncreased
                          ? `-${image.optimized.sizeChangePercentage}% increased`
                          : `${image.optimized.sizeChangePercentage}% reduced`}
                        )
                      </p>
                      {image.optimized.sizeIncreased && (
                        <p className="imgopt-error-note">
                          Note: Size increased due to format limitations; use
                          Scale (%) below 100 for reduction.
                        </p>
                      )}
                    </>
                  )}
                  <div className="imgopt-actions">
                    {!image.optimized ? (
                      <Button
                        className="imgopt-button imgopt-button-primary"
                        onClick={() => {
                          optimizeImage(image.id);
                          const height = document.body.scrollHeight;
                          window.parent.postMessage(
                            { type: "setHeight", height },
                            "*"
                          );
                        }}
                      >
                        Optimize
                      </Button>
                    ) : (
                      <Button
                        className="imgopt-button imgopt-button-success"
                        onClick={() => downloadImage(image.id)}
                      >
                        <FaDownload className="me-1" size={16} />
                        Download
                      </Button>
                    )}
                    <Button
                      className="imgopt-button imgopt-button-danger"
                      onClick={() => removeImage(image.id)}
                    >
                      <FaTrash className="me-1" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default ImageOptimizer;
